import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CASCO_KINDS, CASCO_KIND_LABELS, INSURANCE_INTERVALS, INSURANCE_INTERVAL_LABELS, INSURANCE_STATUS_LABELS, SF_CLASSES, formatCents, sfLabel, type CascoKind, type InsuranceInterval, type InsuranceStatus, type VehicleInsurance, type VehicleInsurances } from '@kontor/shared';
import { api } from '../../api';
import { centsText, parseCents } from '../../money';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';
import { errorText, formatDay } from './shared';
import './fleet.css';

interface Draft {
  insurer: string;
  policyNumber: string;
  validFrom: string;
  validUntil: string;
  open: boolean;
  premium: string;
  interval: InsuranceInterval;
  liabilitySf: string;
  cascoKind: CascoKind;
  cascoSf: string;
  deductiblePartial: string;
  deductibleFull: string;
  note: string;
}

const EMPTY: Draft = { insurer: '', policyNumber: '', validFrom: '', validUntil: '', open: false, premium: '', interval: 'yearly', liabilitySf: '', cascoKind: 'none', cascoSf: '', deductiblePartial: '', deductibleFull: '', note: '' };
const draftOf = (c: VehicleInsurance): Draft => ({
  insurer: c.insurer,
  policyNumber: c.policyNumber ?? '',
  validFrom: c.validFrom,
  validUntil: c.validUntil ?? '',
  open: c.validUntil === null,
  premium: c.premiumCents !== null ? centsText(c.premiumCents) : '',
  interval: c.premiumInterval ?? 'yearly',
  liabilitySf: c.liabilitySf ?? '',
  cascoKind: c.cascoKind,
  cascoSf: c.cascoSf ?? '',
  deductiblePartial: c.deductiblePartialCents !== null ? centsText(c.deductiblePartialCents) : '',
  deductibleFull: c.deductibleFullCents !== null ? centsText(c.deductibleFullCents) : '',
  note: c.note ?? '',
});
const TONE: Record<InsuranceStatus, string> = { current: 'fleet-badge--ok', upcoming: 'fleet-badge--info', ended: 'fleet-badge--off' };

/**
 * Versicherung eines Fahrzeugs (KON-167): Verträge mit Versicherer, Laufzeit, Beitrag und Zahlweise, Schadenfreiheitsklassen (Haftpflicht, Vollkasko) und Kasko-Variante mit
 * Selbstbeteiligung. Verträge eines Fahrzeugs folgen aufeinander. Aus den Beiträgen entsteht die Prognose der monatlichen Kosten in der Flotte, die tatsächlichen Kosten kommen aus den
 * Eingangsrechnungen. Beiträge und Selbstbeteiligung sehen und ändern nur Benutzer mit dem Recht „Werte sehen“.
 */
export function InsurancePanel({ vehicleId }: { vehicleId: string }) {
  const { can } = useSession();
  const client = useQueryClient();
  const values = can('fleet.values');
  const mayWrite = can('fleet.write');
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const list = useQuery({ queryKey: ['fleet-insurances', vehicleId], queryFn: async () => (await api<{ data: VehicleInsurances }>('GET', `/fleet/vehicles/${vehicleId}/insurances`)).data });

  const refresh = () => {
    for (const key of ['fleet-insurances', 'fleet-vehicle', 'fleet-vehicles', 'fleet-overview', 'fleet-vehicle-log']) client.invalidateQueries({ queryKey: [key] });
  };
  const body = (d: Draft) => {
    const premium = values && d.premium.trim() ? parseCents(d.premium) : null;
    const money = (text: string) => (values && text.trim() ? parseCents(text) : null);
    return {
      insurer: d.insurer.trim(),
      policyNumber: d.policyNumber.trim() || null,
      validFrom: d.validFrom,
      validUntil: d.open ? null : d.validUntil || null,
      premiumCents: premium,
      premiumInterval: premium === null ? null : d.interval,
      liabilitySf: d.liabilitySf || null,
      cascoKind: d.cascoKind,
      cascoSf: d.cascoKind === 'full' ? d.cascoSf || null : null,
      deductiblePartialCents: d.cascoKind === 'none' ? null : money(d.deductiblePartial),
      deductibleFullCents: d.cascoKind === 'full' ? money(d.deductibleFull) : null,
      note: d.note.trim() || null,
    };
  };
  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('Nichts zu speichern.');
      const d = editing.draft;
      if (values && d.premium.trim() && parseCents(d.premium) === null) throw new Error('Der Beitrag ist keine gültige Zahl.');
      return editing.id ? api('PUT', `/fleet/insurances/${editing.id}`, body(d)) : api('POST', `/fleet/vehicles/${vehicleId}/insurances`, body(d));
    },
    onSuccess: () => { refresh(); setEditing(null); setError(null); },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Der Vertrag konnte nicht gespeichert werden.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/fleet/insurances/${id}`),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e, 'Das Löschen hat nicht geklappt.')),
  });
  const set = (patch: Partial<Draft>) => setEditing((cur) => (cur ? { ...cur, draft: { ...cur.draft, ...patch } } : cur));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };
  const startNew = () => {
    // Der neue Vertrag beginnt am Tag nach dem Ende des letzten
    const last = list.data?.contracts.filter((c) => c.validUntil).sort((a, b) => b.validUntil!.localeCompare(a.validUntil!))[0];
    const from = last?.validUntil ? new Date(Date.parse(`${last.validUntil}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10) : '';
    setEditing({ id: null, draft: { ...EMPTY, validFrom: from } });
    setOpen(true);
    setError(null);
  };
  const d = editing?.draft;
  const monthly = list.data?.monthlyCents ?? null;

  return (
    <CollapsibleCard title="Versicherung" actions={mayWrite && !editing ? <button type="button" className="btn btn--secondary btn--sm" onClick={startNew}>Vertrag anlegen</button> : undefined} open={open} onOpenChange={setOpen}>
      {values && monthly !== null && <p className="small">Aktuelle Kosten laut Vertrag: <strong>{formatCents(monthly)}</strong> im Monat, <strong>{formatCents(monthly * 12)}</strong> im Jahr. Das ist eine Prognose, die tatsächlichen Kosten kommen aus den Eingangsrechnungen.</p>}
      {error && !editing && <div className="alert alert--error" role="alert">{error}</div>}

      {editing && d && (
        <form className="fleet-form" onSubmit={submit} aria-label="Versicherungsvertrag">
          <h3>{editing.id ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h3>
          {error && <div className="alert alert--error" role="alert">{error}</div>}
          <div className="form-grid">
            <label className="field"><span>Versicherer</span><input className="input" value={d.insurer} onChange={(e) => set({ insurer: e.target.value })} maxLength={100} required /></label>
            <label className="field"><span>Versicherungsscheinnummer (optional)</span><input className="input" value={d.policyNumber} onChange={(e) => set({ policyNumber: e.target.value })} maxLength={60} /></label>
            <label className="field"><span>Vertrag von</span><input className="input" type="date" value={d.validFrom} onChange={(e) => set({ validFrom: e.target.value })} required /></label>
            <div className="field">
              <span>Vertrag bis</span>
              <input className="input" type="date" aria-label="Vertrag bis" value={d.open ? '' : d.validUntil} min={d.validFrom} onChange={(e) => set({ validUntil: e.target.value })} disabled={d.open} required={!d.open} />
              <label className="fleet-check"><input type="checkbox" checked={d.open} onChange={(e) => set({ open: e.target.checked })} /> Unbefristet (verlängert sich von selbst)</label>
            </div>
            {values && (
              <>
                <label className="field"><span>Beitrag je Zahlung (€)</span><input className="input" inputMode="decimal" value={d.premium} onChange={(e) => set({ premium: e.target.value })} placeholder="Zum Beispiel 1.200,00" /></label>
                <label className="field">
                  <span>Zahlweise</span>
                  <select className="input" value={d.interval} onChange={(e) => set({ interval: e.target.value as InsuranceInterval })} disabled={!d.premium.trim()}>
                    {INSURANCE_INTERVALS.map((i) => <option key={i} value={i}>{INSURANCE_INTERVAL_LABELS[i]}</option>)}
                  </select>
                </label>
              </>
            )}
            <label className="field">
              <span>SF-Klasse Haftpflicht</span>
              <select className="input" value={d.liabilitySf} onChange={(e) => set({ liabilitySf: e.target.value })}>
                <option value="">Keine Angabe</option>
                {SF_CLASSES.map((c) => <option key={c} value={c}>{sfLabel(c)}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Kasko</span>
              <select className="input" value={d.cascoKind} onChange={(e) => set({ cascoKind: e.target.value as CascoKind })}>
                {CASCO_KINDS.map((k) => <option key={k} value={k}>{CASCO_KIND_LABELS[k]}</option>)}
              </select>
            </label>
            {d.cascoKind === 'full' && (
              <label className="field">
                <span>SF-Klasse Vollkasko</span>
                <select className="input" value={d.cascoSf} onChange={(e) => set({ cascoSf: e.target.value })}>
                  <option value="">Keine Angabe</option>
                  {SF_CLASSES.map((c) => <option key={c} value={c}>{sfLabel(c)}</option>)}
                </select>
              </label>
            )}
            {values && d.cascoKind !== 'none' && <label className="field"><span>Selbstbeteiligung Teilkasko (€)</span><input className="input" inputMode="decimal" value={d.deductiblePartial} onChange={(e) => set({ deductiblePartial: e.target.value })} /></label>}
            {values && d.cascoKind === 'full' && <label className="field"><span>Selbstbeteiligung Vollkasko (€)</span><input className="input" inputMode="decimal" value={d.deductibleFull} onChange={(e) => set({ deductibleFull: e.target.value })} /></label>}
            <label className="field field--wide"><span>Notiz (optional)</span><input className="input" value={d.note} onChange={(e) => set({ note: e.target.value })} maxLength={500} /></label>
          </div>
          {!values && <p className="muted small">Beitrag und Selbstbeteiligung erfassen nur Benutzer mit dem Recht „Werte sehen“.</p>}
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
            <button type="button" className="btn btn--secondary" onClick={() => { setEditing(null); setError(null); }}>Abbrechen</button>
          </div>
        </form>
      )}

      <div className="table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Versicherer</th><th>Laufzeit</th><th>Schutz</th>{values && <th className="num">Beitrag</th>}<th /></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
            {list.data?.contracts.length === 0 && <tr><td colSpan={5} className="empty">Noch kein Vertrag erfasst. Legen Sie den aktuellen Vertrag an, dann zählt er in der Kostenprognose.</td></tr>}
            {list.data?.contracts.map((c) => (
              <tr key={c.id} className={c.status === 'ended' ? 'inv-reversed' : ''}>
                <td data-label="Versicherer">{c.insurer} <span className={`fleet-badge ${TONE[c.status]}`}>{INSURANCE_STATUS_LABELS[c.status]}</span>{c.policyNumber && <div className="muted small">Nr. {c.policyNumber}</div>}{c.note && <div className="muted small">{c.note}</div>}</td>
                <td data-label="Laufzeit">{formatDay(c.validFrom)} bis {c.validUntil ? formatDay(c.validUntil) : 'unbefristet'}</td>
                <td data-label="Schutz">
                  Haftpflicht{c.liabilitySf ? ` (${sfLabel(c.liabilitySf)})` : ''}
                  {c.cascoKind !== 'none' && <div>{CASCO_KIND_LABELS[c.cascoKind]}{c.cascoKind === 'full' && c.cascoSf ? ` (${sfLabel(c.cascoSf)})` : ''}</div>}
                  {values && c.cascoKind !== 'none' && (c.deductiblePartialCents !== null || c.deductibleFullCents !== null) && (
                    <div className="muted small">Selbstbeteiligung: {[c.deductiblePartialCents !== null ? `${formatCents(c.deductiblePartialCents)} Teilkasko` : null, c.deductibleFullCents !== null ? `${formatCents(c.deductibleFullCents)} Vollkasko` : null].filter(Boolean).join(', ')}</div>
                  )}
                </td>
                {values && (
                  <td data-label="Beitrag" className="num">
                    {c.premiumCents !== null && c.premiumInterval ? (
                      <>{formatCents(c.premiumCents)}<div className="muted small">{INSURANCE_INTERVAL_LABELS[c.premiumInterval].toLowerCase()} · {formatCents(c.monthlyCents ?? 0)} je Monat</div></>
                    ) : (
                      <span className="muted">Beitrag fehlt</span>
                    )}
                  </td>
                )}
                <td className="doc-actions">
                  {mayWrite && <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setEditing({ id: c.id, draft: draftOf(c) }); setError(null); }}>Bearbeiten</button>}
                  {can('fleet.delete') && <button type="button" className="btn btn--danger btn--sm" onClick={() => window.confirm('Diesen Vertrag löschen?') && remove.mutate(c.id)}>Löschen</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleCard>
  );
}
